import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE_NAME, DEFAULT_IMAGE, pageUrl } from "../lib/seo";
import { useBrand } from "../brand/BrandContext";

const defaultDescription = (brand) =>
  `Holiday packages, visa assistance, hotels and cab bookings from ${brand.city}. Plan your next domestic or international trip with ${brand.name}.`;

function setMetaTag(attr, key, content) {
  if (!content) return;
  let tag = document.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

/* Updates title, description, canonical, robots, OG/Twitter tags and JSON-LD for
   the current route. Every routed page should call this so tags from a previous
   page never leak into the next one.
   - noindex: keep private/transactional pages (cart, checkout, quotations…) out of Google.
   - jsonLd: a schema.org object or array of objects, rendered as <script type="application/ld+json">.
   Client-side only: Google renders JS and picks this up; build-time HTML for crawlers
   that don't run JS is produced by scripts/generate-seo.js. */
export default function usePageMeta({ title, description, image, url, noindex = false, jsonLd } = {}) {
  const { pathname } = useLocation();
  // Subscribing to the brand re-renders the page (and its titles) if it changes.
  const brand = useBrand();
  const canonical = url || pageUrl(pathname);
  const jsonLdText = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const fullTitle = title || SITE_NAME;
    const desc = description || defaultDescription(brand);
    const img = image || DEFAULT_IMAGE;

    document.title = fullTitle;
    setMetaTag("name", "description", desc);
    setMetaTag("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    setMetaTag("property", "og:title", fullTitle);
    setMetaTag("property", "og:description", desc);
    setMetaTag("property", "og:image", img);
    setMetaTag("property", "og:url", canonical);
    setMetaTag("name", "twitter:title", fullTitle);
    setMetaTag("name", "twitter:description", desc);
    setMetaTag("name", "twitter:image", img);
    setCanonical(canonical);
    // Drop JSON-LD baked into the HTML at build time; pages re-add their own below.
    document.querySelectorAll("script[data-static-seo]").forEach((s) => s.remove());
  }, [title, description, image, canonical, noindex, brand]);

  useEffect(() => {
    if (!jsonLdText) return undefined;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-page-jsonld", "");
    script.text = jsonLdText;
    document.head.appendChild(script);
    return () => script.remove();
  }, [jsonLdText]);
}
